import { Box, Button, makeStyles } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { CellSize } from "Components/Calendar/CalendarHelper";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { useModalController } from "Components/Modal/Modal";
import { CalendarType } from "Components/Navigation/NavigationHelper";
import { getProjectsBetweenMoments } from "Components/Project/ProjectHelper";
import { useProjects } from "Components/Project/hooks/useProjects";
import ScheduleEntryTypesModal from "Components/ScheduleEntryType/ScheduleEntryTypesModal";
import { useScheduleEntryTypes } from "Components/ScheduleEntryType/hooks/useScheduleEntryTypes";
import { useSchedulePhases } from "Components/SchedulePhase/hooks/useSchedulePhases";
import { color, px } from "Configs/theme";
import { getColorHex } from "Helpers/ColorHelper";
import { useTranslation } from "Providers/TranslationProvider";
import ErrorView from "Views/ErrorView";
import { ScheduleEntryTypeCategory } from "domain-entities";
import React, { CSSProperties, FC, Fragment, useState } from "react";
import {
	QUICK_ADD_HEIGHT,
	QuickAdd,
	TAB_HEIGHT,
	getQuickAddViewKey,
	useGetQuickAddsFromScheduleEntryTypes,
} from "./QuickAddHelper";
import QuickAddView from "./QuickAddView";

interface Props {
	cellSize: CellSize;
	calendarType: CalendarType;
}

interface Tab {
	title: string;
	quickAdds: QuickAdd[];
	isEditVisible?: boolean;
	isProjects?: boolean;
}

const MAX_PAGE_ITEMS = 4;

const QuickAddMenuView: FC<Props> = (props) => {
	const classes = useStyles();
	const getQuickAdds = useGetQuickAddsFromScheduleEntryTypes();
	const scheduleEntryTypes = useScheduleEntryTypes((state) => state.items);
	const isInitializingScheduleEntryTypes = useScheduleEntryTypes(
		(state) => state.isInitializing
	);
	const scheduleEntryTypesError = useScheduleEntryTypes((state) => state.error);
	const [selectedTabIndex, setSelectedTabIndex] = useState(2);
	const [selectedPageIndex, setSelectedPageIndex] = useState(0);
	const scheduleEntryTypesMC = useModalController();
	const { t } = useTranslation();
	const projects = useProjects((state) => state.projects);
	const isInitializingProjects = useProjects((state) => state.isInitializing);
	const schedulePhases = useSchedulePhases((state) => state.items);
	const startOfWeek = useCalendar((state) => state.startOfWeek);
	const projectsOfCalendar = getProjectsBetweenMoments(
		projects,
		schedulePhases,
		startOfWeek,
		startOfWeek
			.clone()
			.add(
				props.calendarType === CalendarType.WEEK
					? 1
					: props.cellSize.month.weeksLength,
				"week"
			)
	);

	const tabs: Tab[] = [
		{
			title: t("QuickAddMenuView_Tab_Project"),
			isProjects: true,
			quickAdds: projectsOfCalendar.map((project) => ({
				name: project.name,
				colorHex: getColorHex(project.colorTag),
				projectId: project.id,
			})),
		},
		{
			title: t("QuickAddMenuView_Tab_Resource"),
			isEditVisible: true,
			quickAdds: getQuickAdds(
				scheduleEntryTypes,
				ScheduleEntryTypeCategory.RESOURCE
			),
		},
		{
			title: t("QuickAddMenuView_Tab_User"),
			isEditVisible: true,
			quickAdds: getQuickAdds(
				scheduleEntryTypes,
				ScheduleEntryTypeCategory.USER
			),
		},
	];

	const selectedTab = tabs[selectedTabIndex];

	function setNextPage(pageIndex: number) {
		if (
			(selectedPageIndex + pageIndex) * MAX_PAGE_ITEMS >=
			selectedTab.quickAdds.length
		) {
			setSelectedPageIndex(0);
		} else if (selectedPageIndex + pageIndex < 0) {
			setSelectedPageIndex(
				Math.floor((selectedTab.quickAdds.length - 1) / MAX_PAGE_ITEMS)
			);
		} else {
			setSelectedPageIndex(selectedPageIndex + pageIndex);
		}
	}

	function renderTabs() {
		return tabs.map((tab, index) => (
			<Box
				key={index}
				className={`${classes.tab} ${
					index === selectedTabIndex ? "selected" : ""
				}`}
				onClick={() => {
					setSelectedTabIndex(index);
					setSelectedPageIndex(0);
				}}
			>
				{tab.title}
			</Box>
		));
	}

	function renderQuickAdds() {
		const quickAddIndex = selectedPageIndex * MAX_PAGE_ITEMS;

		return selectedTab.quickAdds
			.slice(quickAddIndex, quickAddIndex + MAX_PAGE_ITEMS)
			.map((quickAdd) => (
				<QuickAddView
					key={getQuickAddViewKey(quickAdd)}
					quickAdd={quickAdd}
					cellSize={props.cellSize}
				/>
			));
	}

	function renderEditButton() {
		return (
			<Button
				color="primary"
				disabled={!selectedTab.isEditVisible}
				onClick={scheduleEntryTypesMC.open}
			>
				{t("QuickAddMenuView_EditScheduleEntryTypes")}
			</Button>
		);
	}

	function renderPageButtons() {
		const isVisible = selectedTab.quickAdds.length > MAX_PAGE_ITEMS;
		const style: CSSProperties | undefined = isVisible
			? undefined
			: { cursor: "default", opacity: 0 };

		return (
			<Fragment>
				<Button
					className={classes.chevronButton}
					style={style}
					variant="outlined"
					onClick={isVisible ? () => setNextPage(-1) : undefined}
				>
					<ChevronLeftIcon />
				</Button>
				<Button
					className={classes.chevronButton}
					style={style}
					variant="outlined"
					onClick={isVisible ? () => setNextPage(1) : undefined}
				>
					<ChevronRightIcon />
				</Button>
			</Fragment>
		);
	}

	if (isInitializingProjects || isInitializingScheduleEntryTypes) return null;
	if (scheduleEntryTypesError)
		return <ErrorView error={scheduleEntryTypesError} />;
	return (
		<Fragment>
			<Box className={classes.main}>
				<Box className={classes.tabs}>{renderTabs()}</Box>
				<Box className={classes.shadow} />
				<Box className={classes.content}>
					<Box
						className={classes.quickAdds}
						width={(props.cellSize.week.dayWidth + 8) * MAX_PAGE_ITEMS}
					>
						{renderQuickAdds()}
					</Box>
					{renderPageButtons()}
					{renderEditButton()}
				</Box>
			</Box>
			<ScheduleEntryTypesModal modalController={scheduleEntryTypesMC} />
		</Fragment>
	);
};

export default QuickAddMenuView;

const boxShadow = "0 2px 20px 0 rgba(52, 24, 14, 0.25);";

const useStyles = makeStyles({
	main: {
		zIndex: 10,
		position: "fixed",
		bottom: 0,
		right: 0,
		animation: "$quickAdd 800ms ease",
	},
	tabs: {
		position: "absolute",
		display: "flex",
		flexFlow: "row-reverse",
		bottom: QUICK_ADD_HEIGHT,
		height: TAB_HEIGHT,
		left: 0,
	},
	tab: {
		display: "flex",
		userSelect: "none",
		touchCallout: "none",
		cursor: "pointer",
		alignItems: "center",
		borderTopLeftRadius: px.xs,
		borderTopRightRadius: px.xs,
		padding: `${px.none} ${px.xxl}`,
		backgroundColor: color.totalBackground,
		boxShadow,
		fontSize: "14px",
		fontWeight: "bold",
		letterSpacing: 0,
		"&:not(:last-child)": {
			marginLeft: -8,
			marginLight: -8,
		},
		zIndex: 10,
		marginTop: 6,
		"&.selected": {
			zIndex: 12,
			marginTop: 0,
		},
	},
	shadow: {
		zIndex: 11,
		position: "absolute",
		left: 0,
		right: 0,
		bottom: 0,
		height: QUICK_ADD_HEIGHT,
		backgroundColor: color.totalBackground,
		boxShadow,
	},
	content: {
		zIndex: 13,
		position: "relative",
		display: "flex",
		alignItems: "center",
		paddingLeft: px.xxl,
		paddingRight: 26,
		height: QUICK_ADD_HEIGHT,
		backgroundColor: color.totalBackground,
	},
	quickAdds: {
		display: "flex",
	},
	chevronButton: {
		marginRight: px.s,
		padding: `${px.xs} 0`,
		minWidth: "46px",
	},
	"@keyframes quickAdd": {
		from: { transform: `translateY(${QUICK_ADD_HEIGHT + TAB_HEIGHT}px)` },
		to: { transform: "translateY(0px)" },
	},
});
